import { useEffect, useState } from 'react';

/**
 * App-level live regions (WCAG 2.1 — 4.1.3 Status Messages, Level AA, new in
 * 2.1).
 *
 * 4.1.3 says a status change that does NOT move focus must still reach
 * assistive tech. AntD 5.29's `message` renders no `role="alert"` and no
 * `aria-live`, so every "Saved", "Step added", "Publish failed" toast in the
 * app was announced to precisely nobody — the user gets no confirmation their
 * action worked, and no notice when it didn't.
 *
 * Two regions, because politeness is not one-size-fits-all:
 *  - `polite` waits for a pause in speech. Right for success and info, which
 *    must not interrupt what the user is reading.
 *  - `assertive` interrupts. Reserved for errors, where finding out late is
 *    the whole problem.
 *
 * Both are rendered permanently and empty. A live region has to EXIST in the
 * DOM before its content changes, otherwise the change is not observed —
 * mounting the region and its text together is the classic way to get silence.
 */
type Politeness = 'polite' | 'assertive';

type Listener = (message: string, politeness: Politeness) => void;

const listeners = new Set<Listener>();

/** Announce a transient status message to assistive tech. */
export function announce(message: string, politeness: Politeness = 'polite'): void {
  if (!message.trim()) return;
  for (const listener of listeners) listener(message, politeness);
}

export function LiveRegion() {
  const [polite, setPolite] = useState('');
  const [assertive, setAssertive] = useState('');

  useEffect(() => {
    const listener: Listener = (message, politeness) => {
      const set = politeness === 'assertive' ? setAssertive : setPolite;
      // Clear first so an identical consecutive message still reads as a
      // change. Without this, two failures with the same text announce once.
      set('');
      // rAF rather than a timeout: one frame is enough for the empty render to
      // commit, and it keeps the announcement on the same interaction.
      requestAnimationFrame(() => set(message));
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return (
    <>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {polite}
      </div>
      <div className="sr-only" role="alert" aria-live="assertive" aria-atomic="true">
        {assertive}
      </div>
    </>
  );
}
